import Api from '@teko/api';
import { accessTokenInterceptor } from '@teko/auth';
import { PaymentMethodCode, PaymentMethodGroupCode, PaymentCardType } from './types.es.js';
import { checksumGenerator, checksumGeneratorCreateToken, checksumGeneratorRemoveToken, getDateWithFormatyyMMddHHmm } from './utils.es.js';

const defaultRedirectUrl = 'https://vnshop.stag.teko.vn';
const generateRandom = () => Math.random().toString(36).substring(7);
const cyrb53 = (str, seed = 0) => {
    let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
        ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 =
        Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
            Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 =
        Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
            Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};
const genClientTransactionCode = (orderId, methodGroupCode) => {
    const date = new Date();
    return cyrb53(`${orderId}${methodGroupCode}${date.getDate()}${date.getHours()}${date.getMinutes()}${Math.floor(date.getSeconds() / 2)}`)
        .toString()
        .replace('-', 'm');
};
const getInitPaymentType = (type, bankCode) => {
    if (!type || (['ATM', 'VISA'].includes(type) && bankCode))
        return PaymentCardType.REDIRECT_URL_THANH_TOAN;
    return PaymentCardType.GEN_QR_CONG;
};
class PaymentService {
    constructor(paymentURL, envSecretKey, paymentSettingURL = null) {
        this.initPaymentAIOOffline = async (body) => {
            const checksum = await checksumGenerator(this.envSecretKey, body);
            const requestData = Object.assign(Object.assign({}, body), { checksum });
            const res = await this.paymentApi.post('/payment/init/multi-staff', requestData);
            return res.data;
        };
        this.initPaymentAIOnline = async (body) => {
            var _a;
            const checksum = await checksumGenerator(this.envSecretKey, body);
            const requestData = Object.assign(Object.assign({}, body), { checksum });
            const { data } = await this.paymentApi.post('/payment/init/multi-v2', requestData);
            const paymentData = (_a = data === null || data === void 0 ? void 0 : data.payments) === null || _a === void 0 ? void 0 : _a.installment;
            if (paymentData === null || paymentData === void 0 ? void 0 : paymentData.postData) {
                const postData = paymentData === null || paymentData === void 0 ? void 0 : paymentData.postData;
                const form = document.createElement('form');
                form.action = paymentData === null || paymentData === void 0 ? void 0 : paymentData.returnUrl;
                form.method = 'POST';
                form.target = '_self';
                (postData === null || postData === void 0 ? void 0 : postData.parameters) &&
                    Object.keys(postData === null || postData === void 0 ? void 0 : postData.parameters).forEach((key) => {
                        var _a;
                        const hiddenField = document.createElement('input');
                        hiddenField.type = 'hidden';
                        hiddenField.name = key;
                        hiddenField.value = (_a = postData === null || postData === void 0 ? void 0 : postData.parameters) === null || _a === void 0 ? void 0 : _a[key];
                        form.appendChild(hiddenField);
                    });
                document.body.appendChild(form);
                form.submit();
            }
            return data;
        };
        this.getBankAccounts = async () => {
            var _a;
            const res = await this.paymentApi.get(`/settings/banks/accounts`);
            return (_a = res.data) === null || _a === void 0 ? void 0 : _a.data;
        };
        this.requestCreateToken = async (merchantMethodCode, userId, cardType, successUrl, cancelUrl) => {
            const isLocalhost = successUrl.includes('localhost');
            const payload = {
                merchantMethodCode,
                clientTransactionCode: generateRandom(),
                userId,
                successUrl: isLocalhost ? defaultRedirectUrl : successUrl,
                cancelUrl: isLocalhost ? defaultRedirectUrl : cancelUrl,
                cardType,
                bankCode: '',
            };
            const checksum = await checksumGeneratorCreateToken(this.envSecretKey, payload);
            const { data } = await this.paymentSettingApi.post('/payment/init/tokencreate', Object.assign(Object.assign({}, payload), { checksum }));
            return data;
        };
        this.requestRemoveToken = async (merchantMethodCode, userId, token) => {
            const payload = {
                merchantMethodCode,
                clientTransactionCode: generateRandom(),
                userId,
                token,
            };
            const checksum = await checksumGeneratorRemoveToken(this.envSecretKey, payload);
            const { data } = await this.paymentSettingApi.post('/payment/token/remove', Object.assign(Object.assign({}, payload), { checksum }));
            return data;
        };
        this.payForOrderOnline = async ({ terminalCode, order, userId, successUrl, cancelUrl, payment, loyalty, deposit, }) => {
            var _a;
            const { methodCode, methodGroupCode, merchantCode, type, bankCode, token } = payment;
            const isVnpayQRCode = PaymentMethodCode.VNPAY_QRCODE === methodCode;
            const isDeposit = PaymentMethodCode.COD !== methodCode && deposit && deposit.payNowAmount;
            const loyaltyAmount = (loyalty && loyalty.amount) || 0;
            const orderCode = order.originalCode || order.code;
            const prevPayment = ((_a = order === null || order === void 0 ? void 0 : order.payments) === null || _a === void 0 ? void 0 : _a.reduce((amount, currentPayment) => amount + currentPayment.amount, 0)) || 0;
            const netAmount = order.grandTotal - (order.totalPaid || 0) - loyaltyAmount - prevPayment;
            const cardTypeInfo = methodGroupCode.toUpperCase() === PaymentMethodGroupCode.CARD
                ? Object.assign({ type: getInitPaymentType(type, bankCode) }, (methodCode === PaymentMethodCode.VNPAY_TOKEN && { token })) : {};
            const paymentData = Object.assign(Object.assign({ [methodGroupCode.toLowerCase()]: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, payment), { amount: isDeposit ? deposit.payNowAmount : netAmount }), (!!isVnpayQRCode ? {} : cardTypeInfo)), { clientTransactionCode: genClientTransactionCode(orderCode, methodGroupCode) }), (order.qrPromotionAmount && {
                    qrPromotionAmount: order.qrPromotionAmount,
                })) }, (!!loyaltyAmount && {
                loyalty: Object.assign(Object.assign({}, loyalty), { clientTransactionCode: genClientTransactionCode(orderCode, PaymentMethodGroupCode.LOYALTY) }),
            })), (isDeposit &&
                Math.round(deposit.payNowAmount) !== Math.round(netAmount) && {
                credit: {
                    merchantMethodCode: deposit.method.merchantMethodCode,
                    methodCode: deposit.method.methodCode,
                    clientTransactionCode: genClientTransactionCode(orderCode, PaymentMethodGroupCode.CREDIT),
                    amount: netAmount - deposit.payNowAmount,
                },
            }));
            const data = Object.assign(Object.assign({ orderCode,
                userId,
                merchantCode,
                terminalCode, totalPaymentAmount: order.grandTotal - (order.totalPaid || 0), totalPaymentWithoutFee: order.grandTotal - (order.totalPaid || 0) - (order.totalFee || 0), successUrl,
                cancelUrl }, (!!isVnpayQRCode && {
                expiredDate: getDateWithFormatyyMMddHHmm(),
            })), { payments: Object.keys(paymentData)
                    .filter((key) => paymentData[key].amount > 0)
                    .reduce((res, key) => ((res[key] = paymentData[key]), res), {}) });
            return await this.initPaymentAIOnline(data);
        };
        this.paymentURL = paymentURL;
        this.envSecretKey = envSecretKey;
        this.paymentApi = new Api(paymentURL);
        this.paymentApi.addInterceptor(accessTokenInterceptor);
        if (paymentSettingURL) {
            this.paymentSettingApi = new Api(paymentSettingURL);
            this.paymentSettingApi.addInterceptor(accessTokenInterceptor);
        }
    }
}

export { PaymentService };
